import { Injectable } from '@angular/core';
import { GenderCodeType } from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MListData, PassengerType } from '@common/interfaces';
import { MLangCodeConvert } from '@common/interfaces/master/m_lang-code-convert';
import {
  PassportInformationData,
  PassportInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/passport-information.interface';
import { convertStringToDate } from '@lib/helpers';
import { GetOrderResponseDataOrderType } from 'src/sdk-servicing/model/getOrderResponseDataOrderType';
import { Traveler } from 'src/sdk-servicing/model/traveler';
import { TravelerRegulatoryDetails } from 'src/sdk-servicing/model/travelerRegulatoryDetails';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';

/**
 * パスポート情報 service
 */
@Injectable()
export class PassportInformationService {
  constructor(private _s04CommonSvc: PassengerInformationInputModalCommonService) {}

  /**
   * パスポート情報データの配列を初期化します。
   * @returns 初期パスポート情報データを含む配列。
   */
  initialPassportInformationData(): PassportInformationData {
    return {
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      birthDate: '',
      encryptedBirthDate: '',
      nationalityCode: '',
      number: '',
      encryptedNumber: '',
      expiryDate: '',
      issuanceCountryCode: '',
      updateAMCMemberInfo: false,
      isError: false,
    };
  }

  /**
   * パスポート情報部分を初期化します。
   * @returns 最初のパスポート情報部分。
   */
  initialPassportInformationParts(): PassportInformationParts {
    return {
      isDisplay: false,
      isPassportEditable: false,
      registrationClass: '',
      registrationLabel: '',
      order: '',
      isShowGenderAsLabel: false,
      genders: [],
      passengerTypeCode: '',
      selectDate: {
        order: '',
        dateFrom: new Date(),
        dateTo: new Date(),
        initialDate: {
          year: undefined,
          month: undefined,
          day: undefined,
        },
        errorMsg: '',
      },
      selectExpiryDate: {
        order: '',
        initialDate: {
          year: undefined,
          month: undefined,
          day: undefined,
        },
        errorMsg: '',
      },
      isDisplayUpdateAMCMember: false,
      isPassportRegistered: false,
      isPassportInfoRegistered: false,
    };
  }

  /**
   * パスポート情報 初期値の作成
   * @param regulatoryDetails 入国関連情報
   * @returns パスポート情報 初期値
   */
  createData(regulatoryDetails: TravelerRegulatoryDetails): Array<PassportInformationData> {
    return (
      regulatoryDetails.passports?.map((passport) => {
        const naCode =
          this._s04CommonSvc.countryCodeNameList.find(
            (nat) => nat.country2Code === passport?.nationalityCode || nat.country3Code === passport?.nationalityCode
          )?.country2Code ?? '';
        const issuingCode =
          this._s04CommonSvc.countryCodeNameList.find(
            (issuing) =>
              issuing.country2Code === passport?.issuanceCountryCode ||
              issuing.country3Code === passport?.issuanceCountryCode
          )?.country2Code ?? '';
        return {
          firstName: passport.name?.firstName ?? '',
          middleName: passport.name?.middleName ?? '',
          lastName: passport.name?.lastName ?? '',
          gender: passport.gender ?? '',
          birthDate: passport.birthDate ?? '',
          encryptedBirthDate: passport.encryptedBirthDate ?? '',
          nationalityCode: naCode,
          number: passport.number ?? '',
          encryptedNumber: passport.encryptedNumber ?? '',
          expiryDate: passport.expiryDate ?? '',
          issuanceCountryCode: issuingCode,
          updateAMCMemberInfo: false,
          isError: false,
        };
      }) ?? []
    );
  }

  /**
   * 搭乗者基本情報 設定値作成
   * @param traveler 搭乗者情報
   * @param departureDate 出発日
   * @param channelType PNRチャネル区分
   * @returns 搭乗者基本情報 設定値
   */
  createParts(traveler: Traveler, departureDate: Date | null, channelType: string): PassportInformationParts {
    const passports = traveler?.regulatoryDetails?.passports;
    const passportLength = passports?.length ?? 0;
    // パスポート情報編集可否
    const isPassportEditable = !Boolean(
      // PNR情報取得API応答.data.当該travelers.regulatoryDetails.regulatoryDetails.isPassportRegistered=trueそしてPNR情報取得API応答.data.当該travelers.regulatoryDetails.passportsの要素数≧2の場合、又はPNR情報取得API応答.data.当該travelers.hasInfiniNamelessInfant　＝trueの場合、又はPNR情報取得API応答.data.当該travelers.hasNamelessInfant＝trueの場合、パスポート情報の各項目をラベル表示する。
      passportLength >= 2 ||
        (!traveler?.accompanyingTravelerId && traveler?.hasInfiniNamelessInfant) ||
        traveler?.hasNamelessInfant
    );
    let birthday: Date | null = null;
    let expiryDate: Date | null = null;
    let isShowGenderAsLabel: boolean = false;

    if (passportLength > 0) {
      birthday = passports?.[0]?.birthDate ? convertStringToDate(passports?.[0]?.birthDate) : null;
      expiryDate = passports?.[0]?.expiryDate ? convertStringToDate(passports?.[0]?.expiryDate) : null;

      // 当該travelers.passengerTypeCode＝”INF”/""INS""、かつ当該travelers.regulatoryDetails.passports.gender＝”unspecified”,”unknown”の場合、性別コード(XI／UI)をラベル表示し、編集不可とする。
      isShowGenderAsLabel =
        (traveler.passengerTypeCode === PassengerType.INF || traveler.passengerTypeCode === PassengerType.INS) &&
        (passports![0].gender === GenderCodeType.NOT_SPECIFIED || passports![0].gender === GenderCodeType.UNKNOWN);
    }

    const langCodeConvert: MLangCodeConvert | undefined = this._s04CommonSvc.masterData.langCodeConverts.find(
      (item: { lang: string }) => item.lang === this._s04CommonSvc.lang
    );
    const inputOrderType: string = langCodeConvert?.traveler_input_order_type ?? '0';
    const passengerTypeCode: string = traveler.passengerTypeCode ?? '';

    const isPassportInfoRegistered =
      Boolean(traveler.regulatoryDetails?.passports?.[0]?.number) ||
      Boolean(traveler.regulatoryDetails?.passports?.[0]?.issuanceCountryCode) ||
      Boolean(traveler.regulatoryDetails?.passports?.[0]?.expiryDate);
    return {
      isDisplay: true,
      isPassportEditable: isPassportEditable,
      registrationLabel: '',
      registrationClass: '',
      order: inputOrderType,
      isShowGenderAsLabel: isShowGenderAsLabel,
      genders: this.getGenders(traveler.passengerTypeCode ?? ''),
      passengerTypeCode: passengerTypeCode,
      // [1.9] 以下の処理で画面.パスポート生年月日のプルダウンに日付範囲を設定する。
      ...this.createPassportSelectDate(traveler, channelType, departureDate, birthday),
      ...this.createPassportSelectExpiryDate(expiryDate),
      isDisplayUpdateAMCMember: false,
      isPassportRegistered: traveler.regulatoryDetails?.isPassportRegistered ?? false,
      isPassportInfoRegistered,
    };
  }

  /**
   * パスポートの発行日または生年月日を選択するための構造を作成します。
   * @param traveler 搭乗者情報
   * @param channelType PNRチャネル区分
   * @param departureDate 出発日
   * @param birthDay 誕生日
   * @returns
   */
  createPassportSelectDate(traveler: Traveler, channelType: string, departureDate: Date | null, birthDay: Date | null) {
    return {
      selectDate: {
        order: 'YMD', //年月日の順で固定
        dateFrom:
          departureDate !== null
            ? this.getDateFrom(
                traveler.passengerTypeCode ?? '',
                departureDate,
                channelType as GetOrderResponseDataOrderType.ChannelTypeEnum
              )
            : null,
        dateTo:
          departureDate !== null
            ? this.getDateTo(
                traveler.passengerTypeCode ?? '',
                departureDate,
                channelType as GetOrderResponseDataOrderType.ChannelTypeEnum
              )
            : null,
        initialDate:
          birthDay !== null
            ? {
                year: birthDay.getFullYear(),
                month: birthDay.getMonth() + 1,
                day: birthDay.getDate(),
              }
            : {},
        errorMsg: '',
      },
    };
  }

  /**
   * パスポートの有効期限を選択するための構造を作成します。
   * @param expiryDate パスポートの有効期限。
   * @returns パスポートの有効期限を選択するための詳細を含むオブジェクト。
   */
  createPassportSelectExpiryDate(expiryDate: Date | null) {
    return {
      selectExpiryDate: {
        order: 'YMD', //年月日の順で固定
        initialDate:
          expiryDate !== null
            ? {
                year: expiryDate.getFullYear(),
                month: expiryDate.getMonth() + 1,
                day: expiryDate.getDate(),
              }
            : {},
        errorMsg: '',
      },
    };
  }

  /**
   * 搭乗者の生年月日のFromの範囲
   * @param code 搭乗者種別
   * @param departure 出発日
   * @param channelType PNRチャネル区分
   * @returns 搭乗者の生年月日のFrom
   */
  public getDateFrom(code: string, departure: Date, channelType: GetOrderResponseDataOrderType.ChannelTypeEnum) {
    const date = departure ? new Date(departure) : new Date();

    switch (code) {
      case PassengerType.ADT:
        date.setFullYear(date.getFullYear() - 99);
        date.setDate(date.getDate() + 1);
        break;
      case PassengerType.B15:
        channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh
          ? (date.setFullYear(date.getFullYear() - 16), date.setDate(date.getDate() + 1))
          : (date.setFullYear(date.getFullYear() - 99), date.setDate(date.getDate() + 1));
        break;
      case PassengerType.CHD:
        date.setFullYear(date.getFullYear() - 12);
        date.setDate(date.getDate() + 1);
        break;
      case PassengerType.INF:
      case PassengerType.INS:
        date.setFullYear(date.getFullYear() - 2);
        date.setDate(date.getDate() + 1);
        break;
      default:
        break;
    }
    return date;
  }

  /**
   * 搭乗者の生年月日のToの範囲
   * @param code 搭乗者種別
   * @param departure 出発日
   * @param channelType PNRチャネル区分
   * @returns 搭乗者の生年月日のTo
   */
  public getDateTo(code: string, departure: Date, channelType: GetOrderResponseDataOrderType.ChannelTypeEnum) {
    const date = departure ? new Date(departure) : new Date();

    switch (code) {
      case PassengerType.ADT:
      case PassengerType.B15:
        if (channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh) {
          date.setFullYear(date.getFullYear() - 12);
          break;
        } else {
          date.setDate(date.getDate() - 8);
          break;
        }
      case PassengerType.CHD:
        date.setFullYear(date.getFullYear() - 2);
        break;
      case PassengerType.INF:
      case PassengerType.INS:
        date.setDate(date.getDate() - 8);
        break;
      default:
        break;
    }

    return date;
  }

  public getGenders(code: string): MListData[] {
    let genders: MListData[] = [];
    const pd004 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_004' && data.lang === this._s04CommonSvc.lang
    );

    if (code === PassengerType.INF || code === PassengerType.INS) {
      genders = pd004.filter((gender) => gender.value === 'male' || gender.value === 'female');
    } else {
      genders = pd004;
    }
    return genders;
  }
}
